import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ShoppingBag, LogOut, User, LayoutDashboard, Search, ShoppingCart, Filter } from 'lucide-react';
import { toast } from 'sonner';
import FallbackImage from '@/components/FallbackImage';
import SeasonNavigation from '@/components/SeasonNavigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Catalog = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [cart, setCart] = useState([]);
  const [selectedGender, setSelectedGender] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchProducts();
    loadCart();
  }, []);

  useEffect(() => {
    // Filter products based on search, gender, and category
    let filtered = [...products];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.model_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.display_name && p.display_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by gender (kept for backwards compatibility, unused by MACRON catalog)
    if (selectedGender !== 'all') {
      filtered = filtered.filter(p => {
        const gender = (p.genero || '').toLowerCase();
        if (selectedGender === 'hombre') {
          return gender.includes('hombre') || gender.includes('men');
        } else if (selectedGender === 'mujer') {
          return gender.includes('mujer') || gender.includes('women');
        } else if (selectedGender === 'junior') {
          return gender.includes('niño') || gender.includes('junior') || gender.includes('kid');
        }
        return false;
      });
    }

    // Filter by category → maps to Macron familia
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => {
        const familia = (p.familia || '').toLowerCase();
        const subfamilia = (p.subfamilia || '').toLowerCase();
        if (selectedCategory === 'equipacion') return familia.includes('equipación') || familia.includes('equipacion');
        if (selectedCategory === 'entreno') return familia.includes('entrena');
        if (selectedCategory === 'paseo') return familia.includes('paseo') || familia.includes('chándal') || familia.includes('chandal');
        if (selectedCategory === 'accesorios') return familia.includes('accesorio');
        if (selectedCategory === 'jugador') return subfamilia.toLowerCase().includes('jugador');
        if (selectedCategory === 'portero') return subfamilia.toLowerCase().includes('portero');
        if (selectedCategory === 'staff') return subfamilia.toLowerCase().includes('staff');
        return false;
      });
    }

    setFilteredProducts(filtered);
  }, [searchTerm, selectedGender, selectedCategory, products]);

  const loadCart = () => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/products`, { headers });
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const getCartItemCount = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-black border-b border-neutral-800 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <Link to="/" className="flex items-center gap-4">
                <img src="/sudeporte-logo.png" alt="Sudeporte" className="h-9" />
                <span className="hidden sm:block h-8 w-px bg-neutral-700" />
                <img src="/macron-msh-neg-h44.png" alt="Macron Sports Hub" className="hidden sm:block h-7" />
              </Link>
              <span className="hidden lg:inline text-neutral-400 text-xs uppercase tracking-widest ml-2">
                Temporada 26/27 · Elda Unión CF
              </span>
            </div>

            <nav className="flex items-center space-x-2 sm:space-x-4">
              {user && (
                <>
                  <Link to="/my-orders">
                    <Button data-testid="header-orders-btn" variant="ghost" className="text-white hover:bg-neutral-800">
                      <User className="w-4 h-4 mr-2" />
                      Mis Pedidos
                    </Button>
                  </Link>
                  {(user.role === 'admin' || user.role === 'manager') && (
                    <Link to="/admin">
                      <Button data-testid="header-admin-btn" variant="ghost" className="text-white hover:bg-neutral-800">
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        Admin
                      </Button>
                    </Link>
                  )}
                  <Link to="/cart">
                    <Button data-testid="header-cart-btn" variant="outline" className="relative border-white text-white hover:bg-white hover:text-black">
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Carrito
                      {getCartItemCount() > 0 && (
                        <span className="absolute -top-2 -right-2 bg-white text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                          {getCartItemCount()}
                        </span>
                      )}
                    </Button>
                  </Link>
                  <Button data-testid="header-logout-btn" onClick={onLogout} variant="outline" className="border-white text-white hover:bg-white hover:text-black">
                    <LogOut className="w-4 h-4 mr-2" />
                    Salir
                  </Button>
                </>
              )}
              {!user && (
                <Link to="/login">
                  <Button data-testid="header-login-btn" variant="outline" className="border-white text-white hover:bg-white hover:text-black">
                    <User className="w-4 h-4 mr-2" />
                    Iniciar sesión
                  </Button>
                </Link>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Search Bar */}
        <div className="mb-6">
          <div className="max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                data-testid="search-input"
                type="text"
                placeholder="Buscar por modelo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 py-6 text-lg border-gray-300"
              />
            </div>
          </div>
        </div>

        {/* Approval Status Banner - Only show for non-admin users */}
        {user && user.role !== 'admin' && !user.approved && (
          <div className="max-w-4xl mx-auto mb-6 bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4 shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <span className="text-2xl">⏳</span>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-bold text-yellow-900">Cuenta Pendiente de Aprobación</h3>
                <p className="text-sm text-yellow-800 mt-1">
                  Tu cuenta está siendo revisada por nuestro equipo. Actualmente puedes ver el catálogo y precios públicos (PVPR), 
                  pero no podrás realizar pedidos ni ver precios de tarifa mayorista hasta que tu cuenta sea aprobada.
                </p>
                <p className="text-sm text-yellow-800 mt-2 font-semibold">
                  Te notificaremos por email cuando tu cuenta sea activada.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Guest banner — invite to login */}
        {!user && (
          <div className="max-w-4xl mx-auto mb-8 bg-black text-white rounded-xl p-6 border border-black flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-neutral-400">
                Estás viendo el catálogo público
              </p>
              <h2 className="text-xl font-black mt-1">
                Inicia sesión como Deportes Tomás para ver precios y hacer pedido
              </h2>
              <p className="text-neutral-400 text-sm mt-1">
                con el descuento distribuidor autorizado.
              </p>
            </div>
            <Link to="/login">
              <Button className="bg-white text-black hover:bg-neutral-200 font-bold whitespace-nowrap">
                Iniciar sesión
              </Button>
            </Link>
          </div>
        )}


        {/* Filters */}
        <div className="mb-8">
          <div className="flex items-center justify-center mb-4">
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className="border-gray-300"
            >
              <Filter className="w-4 h-4 mr-2" />
              {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
            </Button>
          </div>

          {showFilters && (
            <div className="max-w-4xl mx-auto bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Gender Filter */}
                <div>
                  <h3 className="font-bold text-neutral-900 mb-3">Perfil</h3>
                  <div className="space-y-2">
                    {[
                      { value: 'all', label: 'Todos' },
                      { value: 'jugador', label: 'Jugador' },
                      { value: 'portero', label: 'Portero' },
                      { value: 'staff', label: 'Staff' },
                    ].map(option => (
                      <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="gender"
                          value={option.value}
                          checked={selectedCategory === option.value}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Category Filter */}
                <div>
                  <h3 className="font-bold text-neutral-900 mb-3">Categoría</h3>
                  <div className="space-y-2">
                    {[
                      { value: 'all', label: 'Todas' },
                      { value: 'equipacion', label: 'Equipación Oficial (Gamset)' },
                      { value: 'entreno', label: 'Entrenamiento' },
                      { value: 'paseo', label: 'Paseo / Chándal' },
                      { value: 'accesorios', label: 'Accesorios / Mochilas' },
                    ].map(option => (
                      <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="category"
                          value={option.value}
                          checked={selectedCategory === option.value}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Active Filters Summary */}
              {(selectedGender !== 'all' || selectedCategory !== 'all') && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">
                      Mostrando {filteredProducts.length} producto{filteredProducts.length !== 1 ? 's' : ''}
                    </span>
                    <Button
                      onClick={() => {
                        setSelectedGender('all');
                        setSelectedCategory('all');
                      }}
                      variant="ghost"
                      size="sm"
                      className="text-blue-600 hover:text-blue-700"
                    >
                      Limpiar filtros
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Welcome Message */}
        {user && (
          <div className="mb-8 bg-black text-white border border-black rounded-xl p-6">
            <h2 className="text-xl font-black mb-1">
              Bienvenido, {user.business_name}
            </h2>
            <p className="text-neutral-300 text-sm">
              {user.role === 'admin'
                ? 'Panel administrador · Catálogo MACRON temporada 26/27'
                : (user.approved
                    ? 'Catálogo oficial MACRON — Equipación Elda Unión CF · Temporada 26/27 con tu 24% de descuento aplicado.'
                    : 'Tu cuenta está pendiente de aprobación.'
                  )
              }
            </p>
          </div>
        )}

        {/* Products Grid — Agrupados por categoría con el orden del pedido */}
        {loading ? (
          <div className="text-center py-20">
            <p className="text-neutral-500 text-lg">Cargando productos…</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-neutral-500 text-lg">No se encontraron productos.</p>
          </div>
        ) : (
          <div className="space-y-12">
            {(() => {
              // Orden fijo pedido por el cliente (mismo que el Excel)
              const CATEGORY_ORDER = [
                { key: 'gamset-jugador', label: 'Equipación jugador (Gamset)', short: 'Equip. jugador', match: (p) => matchCat(p, 'equipación', 'jugador') },
                { key: 'gamset-portero', label: 'Equipación portero (Gamset)', short: 'Equip. portero', match: (p) => matchCat(p, 'equipación', 'portero') },
                { key: 'entreno-jugador', label: 'Entreno jugador', short: 'Entreno jugador', match: (p) => matchCat(p, 'entrena', 'jugador') },
                { key: 'entreno-portero', label: 'Entreno portero', short: 'Entreno portero', match: (p) => matchCat(p, 'entrena', 'portero') },
                { key: 'entreno-staff', label: 'Entreno staff', short: 'Entreno staff', match: (p) => matchCat(p, 'entrena', 'staff') },
                { key: 'paseo-jugador', label: 'Paseo jugador', short: 'Paseo jugador', match: (p) => matchCat(p, 'paseo', 'jugador') || matchCat(p, 'chándal', 'jugador') || matchCat(p, 'chandal', 'jugador') },
                { key: 'paseo-staff', label: 'Paseo staff', short: 'Paseo staff', match: (p) => matchCat(p, 'paseo', 'staff') || matchCat(p, 'chándal', 'staff') || matchCat(p, 'chandal', 'staff') },
                { key: 'accesorios', label: 'Accesorios / Mochilas', short: 'Accesorios', match: (p) => matchCat(p, 'accesorio', '') || matchCat(p, 'accesorios', '') },
              ];
              function matchCat(p, familiaLike, subfamiliaLike) {
                const f = (p.familia || '').toLowerCase();
                const s = (p.subfamilia || '').toLowerCase();
                const okF = !familiaLike || f.includes(familiaLike);
                const okS = !subfamiliaLike || s.includes(subfamiliaLike);
                return okF && okS;
              }

              const assigned = new Set();
              const buckets = CATEGORY_ORDER.map((cat) => {
                const items = filteredProducts
                  .filter((p) => !assigned.has(p.id) && cat.match(p))
                  .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
                items.forEach((p) => assigned.add(p.id));
                return { ...cat, items };
              });
              const others = filteredProducts.filter((p) => !assigned.has(p.id));
              if (others.length) buckets.push({ key: 'otros', label: 'Otros', items: others });

              return (
                <>
                  {/* Barra de filtros rápidos — sticky bajo el header */}
                  <div className="sticky top-16 z-40 bg-white/95 backdrop-blur-sm border-y border-neutral-200 -mx-4 px-4 py-3 mb-6" data-testid="category-quicknav">
                    <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
                      <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-black whitespace-nowrap mr-1">Ir a:</span>
                      {buckets.filter(b => b.items.length > 0).map((b) => (
                        <a
                          key={b.key}
                          href={`#cat-${b.key}`}
                          data-testid={`quicknav-${b.key}`}
                          onClick={(e) => {
                            e.preventDefault();
                            const el = document.getElementById(`cat-${b.key}`);
                            if (el) {
                              const y = el.getBoundingClientRect().top + window.scrollY - 140;
                              window.scrollTo({ top: y, behavior: 'smooth' });
                            }
                          }}
                          className="whitespace-nowrap text-xs font-bold px-3 py-2 rounded-full border border-neutral-300 bg-white hover:bg-black hover:text-white hover:border-black transition-colors"
                        >
                          {b.short || b.label} <span className="ml-1 text-[10px] text-neutral-500 font-normal">({b.items.length})</span>
                        </a>
                      ))}
                    </div>
                  </div>

                  {buckets
                    .filter((b) => b.items.length > 0)
                    .map((bucket) => (
                      <section key={bucket.key} id={`cat-${bucket.key}`} data-testid={`category-section-${bucket.key}`} className="scroll-mt-40">
                    <div className="flex items-baseline justify-between mb-4 border-b-2 border-black pb-2">
                      <h2 className="text-2xl font-black text-black uppercase tracking-wider">
                        {bucket.label}
                      </h2>
                      <span className="text-xs uppercase tracking-widest text-neutral-500">
                        {bucket.items.length} referencia{bucket.items.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {bucket.items.map((product) => {
                        const firstSize = product.sizes && product.sizes[0];
                        const showPrices = !!user;
                        const showTariff = user && (user.role === 'admin' || user.approved) && firstSize && firstSize.pt_price;
                        const priceWithIva = showTariff ? firstSize.pt_price * 1.21 : (firstSize ? firstSize.pvpr_price * 1.21 : 0);
                        return (
                          <Link
                            key={product.id}
                            to={`/product/${encodeURIComponent(product.id)}`}
                            data-testid={`product-card-${product.id}`}
                          >
                            <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden hover:border-black transition-all duration-200 hover:-translate-y-1 hover:shadow-lg h-full flex flex-col">
                              <div className="relative h-64 bg-neutral-50">
                                <FallbackImage
                                  images={product.images}
                                  alt={product.display_name || product.model_name}
                                  className="w-full h-full object-contain p-4"
                                />
                                <span className="absolute top-2 left-2 bg-black text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded">
                                  MACRON
                                </span>
                                {showTariff && (
                                  <span className="absolute top-2 right-2 bg-emerald-500 text-white text-[11px] font-black uppercase tracking-widest px-2 py-1 rounded shadow-lg">
                                    −24%
                                  </span>
                                )}
                              </div>
                              <div className="p-4 border-t border-neutral-100 flex-1 flex flex-col">
                                <p className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1">
                                  {product.familia || 'Macron'}{product.subfamilia ? ` · ${product.subfamilia}` : ''}
                                </p>
                                <h3 className="text-base font-black text-black leading-tight">
                                  {product.model_name}
                                </h3>
                                <p className="text-neutral-700 text-sm mb-2">
                                  {product.color}
                                </p>
                                {firstSize?.reference && (
                                  <p className="text-neutral-500 text-xs mb-3">
                                    REF: {firstSize.reference}
                                  </p>
                                )}

                                <div className="mt-auto flex items-end justify-between gap-2">
                                  <div className="flex-1">
                                    {showPrices && firstSize && showTariff && (
                                      <>
                                        <p className="text-xs text-neutral-400 line-through">
                                          PVP Tarifa €{firstSize.pvpr_price.toFixed(2)}
                                        </p>
                                        <p className="text-lg font-black text-black leading-none mt-1">
                                          €{priceWithIva.toFixed(2)}
                                        </p>
                                        <p className="text-[10px] text-neutral-500 uppercase tracking-wider mt-0.5">
                                          IVA incluido · −24% dto
                                        </p>
                                      </>
                                    )}
                                    {showPrices && firstSize && !showTariff && (
                                      <>
                                        <p className="text-lg font-black text-black leading-none">
                                          €{(firstSize.pvpr_price * 1.21).toFixed(2)}
                                        </p>
                                        <p className="text-[10px] text-neutral-500 uppercase tracking-wider mt-0.5">
                                          Tarifa IVA incluido
                                        </p>
                                      </>
                                    )}
                                    {!showPrices && (
                                      <p className="text-[11px] uppercase tracking-widest text-neutral-500">
                                        🔒 Inicia sesión para ver precios
                                      </p>
                                    )}
                                  </div>
                                  <Button data-testid={`view-product-btn-${product.id}`} size="sm" className="bg-black hover:bg-neutral-800 text-white font-bold">
                                    Ver
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </section>
                  ))}
                </>
              );
            })()}
          </div>
        )}
      </main>
    </div>
  );
};

export default Catalog;

// Build refresh: 1776752355
